import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CalendarDays, CheckCircle2, CircleDollarSign, PackageCheck, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { loadMasterPackages, loadPackageItems, loadActiveServices, saveMasterPackage, deleteMasterPackage } from './data.js';
import { supabase, CLINIC_ID } from './supabase.js';
import { dateLabel, friendlyError, number, rupiah } from './lib/format.js';

const DIVISIONS = [
  { value: 'ALL', label: 'Semua divisi' },
  { value: 'WELLNESS', label: 'Wellness / TCM' },
  { value: 'ESTETIK', label: 'Estetik' },
  { value: 'DENTAL', label: 'Dental' },
  { value: 'DOKTER_UMUM', label: 'Dokter Umum' },
  { value: 'FARMASI', label: 'Farmasi' },
  { value: 'FISIOTERAPI', label: 'Fisioterapi' },
  { value: 'SALON', label: 'Salon' },
];

function divisionLabel(v) {
  return DIVISIONS.find((d) => d.value === v)?.label || v || 'Semua divisi';
}

const emptyForm = {
  package_name: '',
  division: 'ALL',
  package_price: '',
  valid_days: '',
  status: 'ACTIVE',
};

export function PackagesPage({ canWrite, canWriteBilling, onDialog }) {
  const [activeTab, setActiveTab] = useState('master');
  return (
    <div className="page-stack">
      <div className="tab-bar">
        <button
          type="button"
          className={`tab-item ${activeTab === 'master' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('master')}
        >
          <PackageCheck size={15} /> Master Paket
        </button>
        <button
          type="button"
          className={`tab-item ${activeTab === 'pasien' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('pasien')}
        >
          <CircleDollarSign size={15} /> Paket Pasien
        </button>
      </div>
      {activeTab === 'master' ? (
        <MasterPaketTab canWrite={canWrite} />
      ) : (
        <PaketPasienTab canWrite={canWrite} canWriteBilling={canWriteBilling} onDialog={onDialog} />
      )}
      <style>{`
        .tab-bar { display: flex; gap: 0; border-bottom: 2px solid #e8e2d5; margin-bottom: 16px; }
        .tab-item {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 10px 20px; border: none; background: none;
          font-size: 14px; font-weight: 600; color: #9ca3af; cursor: pointer;
          border-bottom: 2px solid transparent; margin-bottom: -2px; transition: all 0.15s;
        }
        .tab-item:hover { color: #6b7280; }
        .tab-active { color: #2f6f4f; border-bottom-color: #2f6f4f; }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  MASTER PAKET TAB
// ═══════════════════════════════════════════════════════════════════

function MasterPaketTab({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formItems, setFormItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [pkgs, svcs] = await Promise.all([loadMasterPackages(), loadActiveServices()]);
      setRows(pkgs);
      setServices(svcs);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => [r.package_name, r.division, r.status].some((v) => String(v || '').toLowerCase().includes(term)));
  }, [rows, search]);

  function setField(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  function resetForm() {
    setForm(emptyForm);
    setFormItems([]);
    setEditingId(null);
    setError('');
  }

  async function startEdit(row) {
    setEditingId(row.id);
    setForm({
      package_name: row.package_name || '',
      division: row.division || 'ALL',
      package_price: String(row.package_price ?? ''),
      valid_days: String(row.valid_days ?? ''),
      status: row.status || 'ACTIVE',
    });
    setError('');
    try {
      const items = await loadPackageItems(row.id);
      setFormItems(items.map((it) => ({
        id: it.id,
        service_id: it.service_id,
        service_name: it.services?.name || '',
        quota: String(it.quota || 1),
      })));
    } catch {
      setFormItems([]);
    }
  }

  function addFormItem() {
    setFormItems((p) => [...p, { id: `new_${Date.now()}`, service_id: '', service_name: '', quota: '1' }]);
  }

  function removeFormItem(idx) {
    setFormItems((p) => p.filter((_, i) => i !== idx));
  }

  function updateFormItem(idx, k, v) {
    setFormItems((p) => p.map((it, i) => {
      if (i !== idx) return it;
      if (k === 'service_id') {
        const svc = services.find((s) => s.id === v);
        return { ...it, service_id: v, service_name: svc?.name || '' };
      }
      return { ...it, [k]: v };
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.package_name.trim()) { setError('Nama paket wajib diisi.'); return; }
    if (!form.package_price || Number(form.package_price) < 0) { setError('Harga paket harus >= 0.'); return; }
    if (formItems.length === 0) { setError('Minimal tambahkan 1 layanan dengan kuota.'); return; }
    if (formItems.some((it) => !it.service_id || Number(it.quota) < 1)) { setError('Semua layanan harus dipilih dan kuota >= 1.'); return; }

    setSaving(true);
    setError('');
    try {
      await saveMasterPackage(
        {
          id: editingId,
          package_name: form.package_name.trim(),
          division: form.division,
          package_price: form.package_price,
          valid_days: form.valid_days || 0,
          status: form.status,
        },
        formItems.map((it) => ({ service_id: it.service_id, quota: it.quota }))
      );
      resetForm();
      await fetchAll();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Hapus master paket "${row.package_name}"? Semua data kuota ikut terhapus.`)) return;
    setError('');
    try {
      await deleteMasterPackage(row.id);
      if (editingId === row.id) resetForm();
      await fetchAll();
    } catch (e) {
      setError(friendlyError(e));
    }
  }

  return (
    <>
      {canWrite ? (
        <article className="panel pkg-form-panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">Master Data</span>
              <h3>{editingId ? 'Edit Master Paket' : 'Tambah Master Paket'}</h3>
            </div>
          </div>
          {error ? <div className="alert alert-danger" style={{ margin: '0 22px' }}>{error}</div> : null}
          <form className="pkg-form" onSubmit={handleSubmit}>
            <div className="pkg-form-main">
              <div className="pkg-field">
                <label>Nama Paket *</label>
                <input value={form.package_name} onChange={(e) => setField('package_name', e.target.value)} placeholder="Contoh: Paket Wellness 10x" />
              </div>
              <div className="pkg-field">
                <label>Divisi / Poli</label>
                <select value={form.division} onChange={(e) => setField('division', e.target.value)}>
                  {DIVISIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div className="pkg-field">
                <label>Harga (Rp) *</label>
                <input type="number" min="0" step="1000" value={form.package_price} onChange={(e) => setField('package_price', e.target.value)} placeholder="0" />
              </div>
              <div className="pkg-field">
                <label>Masa Berlaku (hari)</label>
                <input type="number" min="0" value={form.valid_days} onChange={(e) => setField('valid_days', e.target.value)} placeholder="0 = tanpa batas" />
              </div>
              <div className="pkg-field">
                <label>Status</label>
                <select value={form.status} onChange={(e) => setField('status', e.target.value)}>
                  <option value="ACTIVE">Aktif</option>
                  <option value="INACTIVE">Nonaktif</option>
                </select>
              </div>
            </div>

            {/* Layanan + Kuota */}
            <div className="pkg-items-section">
              <div className="pkg-items-head">
                <strong>Layanan & Kuota</strong>
                {canWrite ? (
                  <button type="button" className="btn-add-item" onClick={addFormItem}>
                    <Plus size={13} /> Tambah Layanan
                  </button>
                ) : null}
              </div>
              {formItems.length === 0 ? (
                <p className="muted" style={{ padding: '12px 0', fontSize: 13 }}>Belum ada layanan. Klik "Tambah Layanan" untuk menambahkan.</p>
              ) : (
                <table className="pkg-items-table">
                  <thead>
                    <tr>
                      <th>Layanan</th>
                      <th style={{ width: 120 }}>Kuota (Sesi)</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formItems.map((it, idx) => (
                      <tr key={it.id}>
                        <td>
                          <select value={it.service_id} onChange={(e) => updateFormItem(idx, 'service_id', e.target.value)}>
                            <option value="">Pilih layanan...</option>
                            {services.map((s) => (
                              <option key={s.id} value={s.id}>{s.name} — {divisionLabel(s.division)}{s.price ? ` · ${rupiah.format(Number(s.price))}` : ''}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input type="number" min="1" value={it.quota} onChange={(e) => updateFormItem(idx, 'quota', e.target.value)} />
                        </td>
                        <td>
                          <button type="button" className="btn-remove-item" onClick={() => removeFormItem(idx)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="pkg-form-actions">
              <button className="button button-primary" disabled={saving}>
                {saving ? 'Menyimpan...' : editingId ? 'Perbarui' : 'Simpan'}
              </button>
              {editingId ? (
                <button type="button" className="button button-quiet" onClick={resetForm}>Batal Edit</button>
              ) : null}
            </div>
          </form>
        </article>
      ) : null}

      <article className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">Master Data</span>
            <h3>Daftar Master Paket</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari paket..."
              style={{ padding: '8px 12px', border: '1px solid #e8e2d5', borderRadius: 8, fontSize: 14, minWidth: 220 }}
            />
            <button className="button button-soft" onClick={fetchAll} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh
            </button>
          </div>
        </div>
        {loading ? (
          <div className="content-loading" style={{ padding: '40px 0' }}><div className="loader" /><span>Memuat master paket...</span></div>
        ) : (
          <table className="pkg-table">
            <thead>
              <tr>
                <th>Nama Paket</th>
                <th>Divisi</th>
                <th>Harga</th>
                <th>Total Sesi</th>
                <th>Berlaku</th>
                <th>Status</th>
                {canWrite ? <th>Aksi</th> : null}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const totalSessions = Number(row.total_sessions || 0);
                return (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.package_name}</strong>
                    </td>
                    <td><span className="status status-neutral">{divisionLabel(row.division)}</span></td>
                    <td>{rupiah.format(Number(row.package_price || 0))}</td>
                    <td>{number.format(totalSessions)} sesi</td>
                    <td>{Number(row.valid_days) > 0 ? `${number.format(Number(row.valid_days))} hari` : 'Tanpa batas'}</td>
                    <td>
                      <span className={`status ${row.status === 'ACTIVE' ? 'status-positive' : 'status-danger'}`}>
                        {row.status === 'ACTIVE' ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    {canWrite ? (
                      <td>
                        <button className="link-button" onClick={() => startEdit(row)}>Edit</button>
                        <button className="link-button link-danger" onClick={() => handleDelete(row)}>Hapus</button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
              {!filteredRows.length ? (
                <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>Belum ada master paket.</td></tr>
              ) : null}
            </tbody>
          </table>
        )}
      </article>

      <style>{`
        .pkg-form-panel { padding: 22px; }
        .pkg-form { padding: 0 22px 22px; }
        .pkg-form-main {
          display: grid; grid-template-columns: 1.5fr 1fr 0.8fr 0.8fr 0.8fr;
          gap: 14px; align-items: end; margin-bottom: 16px;
        }
        .pkg-field { display: flex; flex-direction: column; gap: 6px; }
        .pkg-field label { font-size: 12px; font-weight: 700; color: #6b6555; }
        .pkg-field input, .pkg-field select {
          padding: 9px 12px; border: 1px solid #e8e2d5; border-radius: 8px; font-size: 14px; background: #fff;
        }
        .pkg-items-section { margin-top: 4px; }
        .pkg-items-head {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 8px; font-size: 13px;
        }
        .btn-add-item {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 4px 12px; border-radius: 6px; font-size: 12px; font-weight: 600;
          border: 1.5px dashed #2f6f4f; background: none; color: #2f6f4f; cursor: pointer;
        }
        .btn-remove-item {
          background: none; border: none; cursor: pointer; color: #dc2626; padding: 4px;
        }
        .pkg-items-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .pkg-items-table th {
          text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;
          color: #7a7364; padding: 6px 8px; background: #f8f4ec; border-bottom: 1px solid #e8e2d5;
        }
        .pkg-items-table td { padding: 6px 8px; border-bottom: 1px solid #f1ece0; }
        .pkg-items-table select, .pkg-items-table input {
          width: 100%; padding: 6px 8px; border: 1px solid #e8e2d5; border-radius: 6px; font-size: 13px;
        }
        .pkg-form-actions { display: flex; gap: 10px; align-items: center; margin-top: 14px; }
        .pkg-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .pkg-table th {
          text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em;
          color: #7a7364; padding: 10px 12px; background: #f8f4ec; border-bottom: 1px solid #e8e2d5;
        }
        .pkg-table td { padding: 12px; border-bottom: 1px solid #f1ece0; vertical-align: middle; }
        .link-button { background: none; border: none; padding: 0; margin-right: 10px; color: #2f6f4f; font-weight: 700; cursor: pointer; font-size: 13px; }
        .link-danger { color: #c0392b; }
        @media (max-width: 980px) { .pkg-form-main { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 680px) { .pkg-form-main { grid-template-columns: 1fr; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .status-info { background: #e0f2fe; color: #0369a1; }
      `}</style>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  PAKET PASIEN TAB
// ═══════════════════════════════════════════════════════════════════

function PaketPasienTab({ canWrite, canWriteBilling, onDialog }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('package_page_data', { p_clinic_id: CLINIC_ID });
      if (rpcError) throw rpcError;
      setRows(data || []);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusCounts = useMemo(() => {
    const counts = { ALL: rows.length, ACTIVE: 0, EXHAUSTED: 0, CANCELLED: 0 };
    rows.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status]++; });
    return counts;
  }, [rows]);

  const filtered = useMemo(() => {
    let result = rows;
    if (statusFilter !== 'ALL') result = result.filter((r) => r.status === statusFilter);
    const term = search.trim().toLowerCase();
    if (!term) return result;
    return result.filter((r) => [r.client?.name, r.client?.client_code, r.package?.package_name, r.invoice_number, r.status, r.payment_status].filter(Boolean).some((v) => String(v).toLowerCase().includes(term)));
  }, [rows, statusFilter, search]);

  function handleOpenPayment(row) {
    if (!row.transaction_id) return;
    onDialog('payment', { id: row.transaction_id });
  }

  const STATUS_TABS = [
    { value: 'ALL',       label: 'Semua' },
    { value: 'ACTIVE',    label: 'Aktif' },
    { value: 'EXHAUSTED', label: 'Habis' },
    { value: 'CANCELLED', label: 'Dibatalkan' },
  ];

  return (
    <article className="panel table-panel">
      <div className="panel-head" style={{ padding: '1.25rem 1.25rem 0' }}>
        <div>
          <span className="eyebrow">Pembelian & Transaksi</span>
          <h3>Paket Pasien</h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="status status-neutral">{filtered.length} paket</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari pasien, paket, invoice..."
            style={{ padding: '8px 12px', border: '1px solid #e8e2d5', borderRadius: 8, fontSize: 13, minWidth: 220 }}
          />
          {canWrite && canWriteBilling ? (
            <button className="button button-primary" onClick={() => onDialog('packagePurchase')}>
              <PackageCheck size={16} /> Jual Paket
            </button>
          ) : null}
          <button className="button button-soft" onClick={load} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} style={loading ? { animation: 'spin 0.9s linear infinite' } : {}} /> Refresh
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '10px 1.25rem 0' }}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setStatusFilter(tab.value)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 99, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${statusFilter === tab.value ? '#2f6f4f' : '#dbe8d5'}`,
              background: statusFilter === tab.value ? '#15803d' : '#fffdf8',
              color: statusFilter === tab.value ? '#fff' : '#25372f',
            }}
          >
            {tab.label}
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 20, height: 20, padding: '0 5px', borderRadius: 99,
              background: statusFilter === tab.value ? 'rgba(255,255,255,0.18)' : '#edf7ef',
              color: statusFilter === tab.value ? '#fff' : '#547064',
              fontSize: 11, fontWeight: 700,
            }}>
              {statusCounts[tab.value] || 0}
            </span>
          </button>
        ))}
      </div>

      {error ? (
        <div className="alert alert-danger" style={{ margin: '10px 1.25rem 0' }}>
          <AlertCircle size={16} /> {error}
        </div>
      ) : null}

      {loading ? (
        <div className="content-loading" style={{ padding: '40px 0' }}><div className="loader" /><span>Memuat data paket...</span></div>
      ) : (
        <table className="pkg-table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Pasien</th>
              <th>Paket</th>
              <th>Dibeli</th>
              <th>Berakhir</th>
              <th>Saldo</th>
              <th>Harga</th>
              <th>Pembayaran</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => {
              const paymentStatus = row.transaction_payment_status || row.payment_status || '';
              const ready   = /^(READY_FOR_PAYMENT|SIAP_BAYAR)$/i.test(paymentStatus);
              const paid    = /^(PAID|LUNAS|TERBAYAR)$/i.test(paymentStatus);
              const partial = /^PARTIAL$/i.test(paymentStatus);
              return (
                <tr key={row.id}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{row.client?.name || row.client_id}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{row.client?.client_code || ''}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{row.package?.package_name || row.package_id}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{row.invoice_number || 'Tanpa invoice'}</div>
                  </td>
                  <td>{dateLabel(row.purchase_date)}</td>
                  <td>{row.expired_date ? dateLabel(row.expired_date) : 'Tanpa batas'}</td>
                  <td>
                    <strong>{number.format(Number(row.remaining_sessions || 0))} / {number.format(Number(row.total_sessions || 0))} sesi</strong>
                  </td>
                  <td>{rupiah.format(Number(row.package_price || 0))}</td>
                  <td>
                    <span className={`status ${paid ? 'status-positive' : partial ? 'status-info' : ready ? 'status-warning' : 'status-neutral'}`}>
                      {partial ? 'Cicilan' : paymentStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`status ${row.status === 'ACTIVE' ? 'status-positive' : row.status === 'EXHAUSTED' ? 'status-warning' : row.status === 'CANCELLED' ? 'status-danger' : 'status-neutral'}`}>
                      {row.status}
                    </span>
                  </td>
                  <td>
                    {!row.transaction_id ? (
                      <span className="muted">Data migrasi</span>
                    ) : (!ready && !paid && !partial) ? (
                      <span className="muted">—</span>
                    ) : (
                      <button
                        className={`button button-mini ${(ready || partial) && canWriteBilling ? 'button-primary' : 'button-quiet'}`}
                        onClick={() => handleOpenPayment(row)}
                      >
                        <CircleDollarSign size={14} />
                        {partial && canWriteBilling ? 'Bayar Cicilan' : ready && canWriteBilling ? 'Bayar' : 'Kwitansi'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!filtered.length ? (
              <tr><td colSpan={9} className="muted" style={{ textAlign: 'center', padding: 40 }}>
                <CheckCircle2 size={28} style={{ color: '#22c55e', marginBottom: 6, opacity: 0.6 }} />
                <div style={{ fontWeight: 600 }}>Belum ada paket pasien.</div>
              </td></tr>
            ) : null}
          </tbody>
        </table>
      )}
    </article>
  );
}
